<template>
  <div class="pa-3">
    <v-card elevation="2" style="border-radius: 12px; overflow: hidden;" class="compact-card">
      <v-card-title class="pa-3 text-subtitle-2 font-weight-bold">
        Activity Log
        <v-spacer></v-spacer>
        <v-btn 
          color="primary" 
          small 
          @click="fetchActivities" 
          :loading="loading"
        >
          <v-icon left small>mdi-refresh</v-icon> Refresh
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-data-table
        :headers="headers"
        :items="activities"
        :loading="loading"
        class="elevation-0 compact-table"
        :items-per-page="20"
        :sort-by="['date']"
        :sort-desc="[true]"
        dense
      >
        <template v-slot:item.date="{ item }">
          <span class="font-weight-medium black--text">{{ formatDate(item.date) }}</span>
        </template>

        <template v-slot:item.user="{ item }">
          <v-chip small dark color="#008080">
            {{ item.userName || item.user || 'System' }}
          </v-chip>
        </template>

        <template v-slot:item.eventAction="{ item }">
          <span :class="actionColor(item.eventAction)" class="font-weight-bold">
            {{ item.eventAction }}
          </span>
        </template>

        <template v-slot:item.targetEmail="{ item }">
          <span v-if="item.targetEmail" class="grey--text text--darken-3">
            {{ item.targetEmail }}
          </span>
          <i v-else class="grey--text">—</i>
        </template>

        <template v-slot:item.eventType="{ item }">
          <v-chip small :color="getTypeColor(item.eventType)" dark>
            {{ item.eventType }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";

const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      loading: true,
      activities: [],
      headers: [
        { text: "Date & Time", value: "date", width: "180px" },
        { text: "User", value: "user", width: "170px" },
        { text: "Action", value: "eventAction" },
        { text: "Target", value: "targetEmail" },
        { text: "Type", value: "eventType", width: "140px" },
      ],
    };
  },

  async created() {
    await this.fetchActivities();
  },

  methods: {
    async fetchActivities() {
      this.loading = true;
      try {
        const q = query(collection(db, "events"), orderBy("date", "desc"));
        const snapshot = await getDocs(q);
        this.activities = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error("Error loading activities:", error);
      } finally {
        this.loading = false;
      }
    },

    formatDate(seconds) {
      const date = new Date(seconds * 1000);
      return date.toLocaleString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    actionColor(action) {
      if (!action) return "grey--text text--darken-4";
      if (action.includes("Created") || action.includes("Added")) return "green--text text--darken-2";
      if (action.includes("Updated") || action.includes("Edited") || action.includes("Modified")) return "blue--text text--darken-2";
      if (action.includes("Deleted") || action.includes("Removed") || action.includes("Cancelled")) return "red--text text--darken-3";
      if (action.includes("Logged") || action.includes("Login")) return "purple--text text--darken-2";
      if (action.includes("Export")) return "orange--text text--darken-2";
      return "grey--text text--darken-4";
    },

    getTypeColor(type) {
      if (!type) return "grey";
      const t = type.toLowerCase();
      if (t.includes("property")) return "blue";
      if (t.includes("tenant")) return "green";
      if (t.includes("lease")) return "orange";
      if (t.includes("payment") || t.includes("financial")) return "teal";
      if (t.includes("user") || t.includes("auth")) return "purple";
      if (t.includes("report")) return "indigo";
      return "grey";
    }
  }
};
</script>

<style scoped>
/* Compact Card */
.compact-card {
  border-radius: 12px !important;
}

/* Compact Table */
.compact-table :deep(.v-data-table-header th) {
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 10px 12px !important;
  background-color: #f0f0f0 !important;
  color: #000000 !important;
  border-bottom: 2px solid #ccc !important;
}

.compact-table :deep(.v-data-table-header th span) {
  color: #000000 !important;
}

.compact-table :deep(tbody td) {
  font-size: 0.875rem !important;
  padding: 8px 12px !important;
  color: #000000 !important;
}

.compact-table :deep(tbody td .v-chip) {
  font-size: 0.75rem !important;
  height: 24px !important;
}

.compact-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

.compact-table :deep(.v-data-table__expanded__content) {
  font-size: 0.875rem !important;
}

/* Black text */
.black--text {
  color: #000000 !important;
}

/* Card title */
.text-subtitle-2 {
  font-size: 1rem !important;
  color: #000000 !important;
}

/* Buttons */
.v-btn {
  font-size: 0.875rem !important;
  letter-spacing: 0.3px;
  min-height: 36px !important;
  padding: 0 16px !important;
  text-transform: none !important;
}

.v-btn .v-icon {
  font-size: 18px !important;
}

/* Loading state */
:deep(.v-data-table__progress .v-progress-circular) {
  width: 40px !important;
  height: 40px !important;
}

/* Responsive — keep mobile readable */
@media (max-width: 768px) {
  .compact-table :deep(.v-data-table-header th) {
    font-size: 0.75rem !important;
    padding: 8px 8px !important;
  }

  .compact-table :deep(tbody td) {
    font-size: 0.8rem !important;
    padding: 6px 8px !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>